import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { TableHeader } from 'components/table/table-content';
import { Filters, filterTypes } from 'elements/Filter';
import { PubSub } from 'lib/event/PubSub';
import { FilterManager } from 'lib/tables/FilterManager';
import { AsyncErrorHandler } from 'lib/ui';
import { ErpSystem } from 'models';
import { Models } from 'models/core';
import { ConsumptionMaterialWashingService } from 'services/consumption-material-washing-service';
import { ErpSystemService } from 'services/erp-system-service';

type ConsumableWash = {
  Id: number;
  Name: string;
  ArticleNo: string;
  ErpSystemName: string;
};

@autoinject
export class ConsumptionMaterialWashingList {
  protected tableData: Array<Models.ConsumptionMaterialWashing>;
  protected activeTableRow: number;

  protected erpSystems: ErpSystem[] = [];

  protected tableHeaders: TableHeader<ConsumableWash>[] = [
    { key: 'Name', label: 'general.name', sortable: true },
    { key: 'ArticleNo', label: 'general.articleno', sortable: true },
    { key: 'ErpSystemName', label: 'general.erpSystem', sortable: false },
  ];

  constructor(
    private pubsub: PubSub,
    private router: Router,
    protected erpSystemService: ErpSystemService,
    private eventAggregator: EventAggregator,
    protected fm: FilterManager,
    protected consumptionMaterialWashingService: ConsumptionMaterialWashingService,
    private t: I18N
  ) {
    pubsub.sub('filter-manager:filter-changed', () => {
      this.updateView();
    });
  }

  activate(params: { Id: number }) {
    if (params.Id) {
      this.activeTableRow = params.Id;
    } else {
      this.activeTableRow = 0;
    }
  }

  @AsyncErrorHandler
  async attached() {
    this.eventAggregator.subscribe('consumableWashListReset', () => {
      this.activeTableRow = null;
      return this.updateView();
    });

    await this.load();

    this.fm.init({
      context: 'consumable-wash',
      storageKey: 'consumable-wash',
      filters: {
        erpIds: {
          name: 'erpIds',
          label: this.t.tr('general.erpSystem'),
          type: filterTypes.CHECKBOX,
          options: this.erpSystems,
          query: 'erpIds',
        },
      } satisfies Filters,
      defaultFilters: {
        erpIds: true,
      },
    });
  }

  private async load() {
    const erpSystems = await this.erpSystemService.getAll();
    const newList = erpSystems.map((e) => {
      const newItem = { ...e, Name: e.Title };
      delete newItem.Title;
      return newItem;
    });

    this.erpSystems = newList;
  }

  @AsyncErrorHandler
  async updateView() {
    const response = await this.consumptionMaterialWashingService.getConsumptionMaterialWashingList(this.fm.queryFilter);
    if (!response) return;
    this.fm.paginationTotalRecords = +response.headers.get('x-total-count') || 0;
    this.tableData = response.data;
  }

  protected setOrderByColumn(column: string) {
    this.fm.setOrderByColumn(column);
  }

  gotoDetailView(id: number) {
    this.activeTableRow = id;
    this.router.navigateToRoute('service-consumption-material-washing-detail', { Id: id });
  }

  protected detached() {
    this.pubsub.unsub();
  }

  protected unbind() {
    this.pubsub.unsub();
  }
}
